import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { License } from '../../types/payment.types';
import { useAppSelector, useAppDispatch } from '../../hooks/useRedux';
import { LicenseService } from '../../services/license.service';
import { loadStripe } from '@stripe/stripe-js';
import {
    CardElement,
    Elements,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { updateLicenseType } from '../../store/slices/authSlice';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

interface PaymentFormProps {
    teamId: string;
    memberLimit: number;
    price: number;
    licenseType: 'standard' | 'enterprise';
    onSuccess: (paymentIntentId: string) => void;
    onError: (error: string) => void;
    teamName?: string;
}

const PaymentFormContent: React.FC<PaymentFormProps> = ({ 
    teamId,
    memberLimit,
    price,
    licenseType,
    onSuccess, 
    onError,
    teamName
}) => {
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [clientSecret, setClientSecret] = useState<string>('');
    const { user } = useAppSelector(state => state.auth);
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useAppDispatch();

    useEffect(() => {
        // Show warning if not on HTTPS in production
        if (process.env.NODE_ENV === 'production' && window.location.protocol !== 'https:') {
            setErrorMessage('Warning: Payment should be processed over HTTPS in production.');
            return;
        }

        const createIntent = async () => {
            try {
                if (isNaN(price) || price < 0.50) {
                    throw new Error('Invalid price amount. Minimum payment amount is $0.50');
                }

                setIsProcessing(true);
                // Convert price to cents for Stripe
                const amountInCents = Math.round(price * 100);
                const result = await LicenseService.createPaymentIntent({
                    team_id: teamId || '',
                    user_id: user?.id || '',
                    member_limit: memberLimit,
                    license_type: licenseType,
                    amount: amountInCents
                });
                setClientSecret(result.clientSecret);
            } catch (error) {
                const err = error as Error;
                setErrorMessage(err.message || 'Failed to initialize payment');
                onError(err.message || 'Failed to initialize payment');
                navigate('/payment/error', { 
                    state: { error: err.message || 'Failed to initialize payment' }
                });
            } finally {
                setIsProcessing(false);
            }
        };

        if (price && !isNaN(price)) {
            createIntent();
        }
    }, [teamId, user?.id, memberLimit, licenseType, price]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        
        if (!stripe || !elements || !clientSecret) {
            return;
        }

        setIsProcessing(true);
        setErrorMessage('');

        try {
            const cardElement = elements.getElement(CardElement);
            if (!cardElement) {
                throw new Error('Card element not found');
            }

            const { error: cardError } = await stripe.createToken(cardElement);
            if (cardError) {
                throw new Error(cardError.message);
            }

            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: {
                        email: user?.email,
                    },
                }
            });

            if (error) {
                throw new Error(error.message);
            }

            if (paymentIntent.status === 'succeeded') {
                const result = await LicenseService.processPayment({
                    payment_intent_id: paymentIntent.id,
                    team_id: teamId || '',
                    user_id: user?.id || '',
                    member_limit: memberLimit,
                    license_type: licenseType
                });

                if (result.success) {
                    // Update the license type in Redux
                    dispatch(updateLicenseType('paid'));
                    
                    
                    onSuccess(paymentIntent.id);
                    navigate('/payment/success', {
                        state: {
                            teamName,
                            licenseType,
                            memberLimit
                        }
                    });
                } else {
                    throw new Error(result.message);
                }
            } else if (paymentIntent.status === 'requires_action') {
                const { error: actionError } = await stripe.handleCardAction(clientSecret);
                if (actionError) {
                    throw new Error(actionError.message);
                }
                handleSubmit(event);
            } else {
                throw new Error('Payment failed. Please try again.');
            }
        } catch (err) {
            const error = err as Error;
            setErrorMessage(error.message || 'An unexpected error occurred');
            onError(error.message || 'Payment failed');
            navigate('/payment/error', { 
                state: { error: error.message || 'Payment failed' }
            });
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="max-w-md mx-auto p-6">
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                    Purchase {licenseType.charAt(0).toUpperCase() + licenseType.slice(1)} License
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                    One-time payment for {memberLimit} team members
                </p>
            </div>

            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Payment Summary</h3>
                <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-300">License Type:</span>
                    <span className="text-gray-900 dark:text-white">
                        {licenseType.charAt(0).toUpperCase() + licenseType.slice(1)}
                    </span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                    <span className="text-gray-500 dark:text-gray-300">Member Limit:</span>
                    <span className="text-gray-900 dark:text-white">{memberLimit}</span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                    <span className="text-gray-500 dark:text-gray-300">Amount:</span>
                    <span className="text-gray-900 dark:text-white">${price.toFixed(2)}</span>
                </div>
            </div>

            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Card Details
                </label>
                <div className="p-4 border rounded-md bg-white dark:bg-gray-800">
                    <CardElement
                        options={{
                            style: {
                                base: {
                                    fontSize: '16px',
                                    color: '#424770',
                                    '::placeholder': {
                                        color: '#aab7c4',
                                    },
                                },
                                invalid: {
                                    color: '#9e2146',
                                },
                            },
                            hidePostalCode: true
                        }}
                    />
                </div>
            </div>

            {errorMessage && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                    {errorMessage}
                </div>
            )}

            <button
                type="submit"
                disabled={isProcessing || !stripe || !clientSecret}
                className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    (isProcessing || !stripe || !clientSecret) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
                {isProcessing ? 'Processing...' : `Pay $${price.toFixed(2)}`}
            </button>
        </form>
    );
};

// Wrap the form with Stripe Elements
const PaymentForm: React.FC<PaymentFormProps> = (props) => {
    return (
        <Elements stripe={stripePromise}>
            <PaymentFormContent {...props} />
        </Elements>
    );
};

export default PaymentForm; 